import React from "react";
import { Input } from "@/components/ui/input";
import { Sparkles, Swords, PawPrint } from "lucide-react";

const SPELL_FIELDS = ["COST", "RNG", "AOE", "POW", "DUR", "OFF"];

const BLOCKS = {
  spell: { title: "Spell card", icon: Sparkles, fields: SPELL_FIELDS, tone: "emerald" },
  animus: { title: "Animus card", icon: PawPrint, fields: SPELL_FIELDS, tone: "emerald" },
  weaponAbility: { title: "Weapon profile carrying this quality", icon: Swords, tone: "amber" },
};

const weaponFields = (type) => (type === "Ranged" ? ["RNG", "ROF", "AOE", "POW"] : ["RNG", "POW"]);

export const hasStatBlock = (ruleType) => Boolean(BLOCKS[ruleType]);

/** Only the filled-in fields relevant to this rule type, or null. */
export function cleanStats(ruleType, stats) {
  if (!hasStatBlock(ruleType) || !stats) return null;
  const keys = ruleType === "weaponAbility"
    ? ["Weapon", "Type", ...weaponFields(stats.Type || "Melee")]
    : BLOCKS[ruleType].fields;
  const out = {};
  for (const k of keys) {
    const v = String(stats[k] ?? "").trim();
    if (v) out[k] = v;
  }
  const hasNumbers = Object.keys(out).some((k) => k !== "Type" && k !== "Weapon");
  return hasNumbers || out.Weapon ? out : null;
}

function StatCell({ label, value, onChange, disabled, testId }) {
  return (
    <label className="flex flex-col items-center gap-0.5">
      <span className="text-[9px] tracking-wide text-muted-foreground">{label}</span>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="-"
        disabled={disabled}
        className="h-8 w-14 px-1 text-center font-mono font-semibold"
        data-testid={testId}
      />
    </label>
  );
}

function TypeToggle({ value, onChange, disabled, testId }) {
  return (
    <div className="inline-flex rounded border border-border overflow-hidden text-xs" data-testid={testId}>
      {["Melee", "Ranged"].map((t) => (
        <button
          key={t}
          type="button"
          disabled={disabled}
          onClick={() => onChange(t)}
          className={`px-2.5 py-1 transition-colors ${value === t ? (t === "Ranged" ? "bg-emerald-800/60 text-emerald-100" : "bg-amber-800/60 text-amber-100") : "text-muted-foreground hover:bg-card"}`}
          data-testid={`${testId}-${t.toLowerCase()}`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/** Fillable in-game style stat block for spell / animus / weapon proposals. */
export function StatBlockEditor({ ruleType, value, onChange, disabled, testId = "stat-block" }) {
  const block = BLOCKS[ruleType];
  if (!block) return null;
  const stats = value || {};
  const set = (k, v) => onChange({ ...stats, [k]: v });
  const Icon = block.icon;
  const isWeapon = ruleType === "weaponAbility";
  const type = stats.Type || "Melee";
  const fields = isWeapon ? weaponFields(type) : block.fields;
  const border = block.tone === "amber" ? "border-amber-800/50 bg-amber-950/15" : "border-emerald-800/50 bg-emerald-950/15";
  return (
    <div className={`rounded-md border ${border} p-3`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium">{block.title}</span>
        <span className="text-[11px] text-muted-foreground">optional — sent to the LLM with the rule text</span>
      </div>
      {isWeapon && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Input
            value={stats.Weapon || ""}
            onChange={(e) => set("Weapon", e.target.value)}
            placeholder="Weapon name…"
            disabled={disabled}
            className="h-8 max-w-[220px] text-sm"
            data-testid={`${testId}-weapon-name`}
          />
          <TypeToggle value={type} onChange={(t) => set("Type", t)} disabled={disabled} testId={`${testId}-weapon-type`} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {fields.map((k) => (
          <StatCell key={k} label={k} value={stats[k]} onChange={(v) => set(k, v)} disabled={disabled} testId={`${testId}-${k.toLowerCase()}`} />
        ))}
      </div>
    </div>
  );
}
